import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import InputAdornment from "@material-ui/core/InputAdornment";
import Icon from "@material-ui/core/Icon";
import { useNavigate, useLocation } from "react-router-dom";
import qs from "query-string";
import { client } from "../../services/axiosClient";
import { useTranslation } from "react-i18next";

// core components
import GridContainer from "../../components/Grid/GridContainer.js";
import GridItem from "../../components/Grid/GridItem.js";
import CustomInput from "../../components/CustomInput/CustomInput.js";
import Button from "../../components/CustomButtons/Button.js";
import Card from "../../components/Card/Card.js";
import CardBody from "../../components/Card/CardBody.js";
import CardHeader from "../../components/Card/CardHeader.js";
import CardFooter from "../../components/Card/CardFooter.js";
import LoaderBar from "../../components/Loaders/LoaderBar.js";
import styles from "../../assets/jss/material-dashboard-pro-react/views/loginPageStyle.js";
const RESET_PASSWORD_CHANGE_ROUTE = "/api/password-reset/confirm/";

const useStyles = makeStyles(styles);

export default function PasswordReset() {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const { uid, token } = qs.parse(location.search, { ignoreQueryPrefix: true });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    if (!uid || !token) {
      setError(t("password_reset.error_invalid_link"));
      setIsSubmitting(false);
      return;
    }

    const formData = new FormData(e.target);
    const password = formData.get("password");
    const passwordConfirm = formData.get("password_confirm");

    if (password !== passwordConfirm) {
      setError(t("password_reset.errors.passwords_mismatch"));
      setIsSubmitting(false);
      return;
    }

    try {
      await client.post(RESET_PASSWORD_CHANGE_ROUTE, {
        uid,
        token,
        password,
      });
      setResetSuccess(true);
    } catch (err) {
      if (err.response?.data?.error) {
        setError(t(`password_reset.errors.${err.response.data.error}`));
      } else {
        setError(t("password_reset.errors.ERROR_DEFAULT"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (resetSuccess) {
    return (
      <div className={classes.container}>
        <GridContainer justifyContent="center">
          <GridItem xs={12} sm={8} md={4}>
            <Card>
              <CardHeader
                className={`${classes.cardHeader} ${classes.textCenter}`}
                color="integration"
              >
                <h4 className={classes.cardTitle}>
                  {t("password_reset.success_title")}
                </h4>
              </CardHeader>
              <CardBody>
                <p className={classes.textCenter}>
                  {t("password_reset.success_message")}
                </p>
                <div className={classes.textCenter}>
                  <Button
                    color="integration"
                    simple
                    size="lg"
                    onClick={() => navigate("/auth/login")}
                  >
                    {t("password_reset.go_to_login")}
                  </Button>
                </div>
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <GridContainer justifyContent="center">
        <GridItem xs={12} sm={8} md={4}>
          <form onSubmit={handleSubmit}>
            <Card>
              <CardHeader
                className={`${classes.cardHeader} ${classes.textCenter}`}
                color="integration"
              >
                <h4 className={classes.cardTitle}>
                  {t("password_reset.title")}
                </h4>
              </CardHeader>
              <CardBody>
                <p className={classes.textCenter}>
                  {t("password_reset.enter_new_password")}
                </p>
                <CustomInput
                  labelText={t("password_reset.new_password")}
                  id="password"
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Icon className={classes.inputAdornmentIcon}>
                          lock_outline
                        </Icon>
                      </InputAdornment>
                    ),
                    type: "password",
                    name: "password",
                    disabled: isSubmitting,
                    autoComplete: "new-password",
                    required: true,
                    // minLength: 8
                  }}
                  integration
                />
                <CustomInput
                  labelText={t("password_reset.confirm_password")}
                  id="password_confirm"
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Icon className={classes.inputAdornmentIcon}>
                          lock_outline
                        </Icon>
                      </InputAdornment>
                    ),
                    type: "password",
                    name: "password_confirm",
                    disabled: isSubmitting,
                    autoComplete: "new-password",
                    required: true,
                    // minLength: 8
                  }}
                  integration
                />
                {error && (
                  <div className={classes.loginErrorMessage}>{error}</div>
                )}
              </CardBody>
              <CardFooter className={classes.justifyContentCenter}>
                <GridContainer>
                  <GridItem xs={12}>
                    <Button
                      type="submit"
                      color="integration"
                      simple
                      size="lg"
                      block
                      disabled={isSubmitting}
                    >
                      {t("password_reset.reset_password")}
                    </Button>
                  </GridItem>
                  <GridItem xs={12}>
                    <Button
                      color="integration"
                      simple
                      block
                      href="/auth/login"
                      disabled={isSubmitting}
                    >
                      {t("password_reset.back_to_login")}
                    </Button>
                  </GridItem>
                </GridContainer>
              </CardFooter>
              {isSubmitting && <LoaderBar />}
            </Card>
          </form>
        </GridItem>
      </GridContainer>
    </div>
  );
}
